import type { ReactNode } from 'react'
import { cn } from '../lib/utils'

/** Which rung of the page hierarchy a header belongs to. A sub-page title is always the smaller. */
export enum PageHeaderTier {
  /** A tab / nav destination — Home, Meal Plans, Recipes, Eating Style, Your plans, Settings. */
  Top = 'top',
  /** Anything reached from one of those — a settings page, What's new, the plan editor. */
  Sub = 'sub'
}

export const PAGE_HEADER_TITLE_SIZE: Record<PageHeaderTier, string> = {
  [PageHeaderTier.Top]: 'text-display-lg',
  [PageHeaderTier.Sub]: 'text-2xl'
}

export interface PageHeaderProps {
  tier: PageHeaderTier
  /** The page's single `<h1>`. Rich text, so one word can take an `<em>` accent. */
  title: ReactNode
  /** A short real label above the title ("Your library"), or a status chip in its place. */
  eyebrow?: ReactNode
  /** One line under the title saying what the page is for. */
  description?: ReactNode
  /** Controls, chips or stats beside the title; they wrap below it when the row runs out of room. */
  actions?: ReactNode
  /** Extra classes for the actions slot, e.g. `ml-auto` to keep wrapped actions on the right. */
  actionsClassName?: string
  className?: string
}

// Eyebrow, title, description and an actions slot. Owns the gap below the app's top bar,
// so pages render it first and add no top padding of their own.
export function PageHeader({ tier, title, eyebrow, description, actions, actionsClassName, className }: PageHeaderProps) {
  return (
    <header className={cn('mb-6 pt-4 md:mb-8 md:pt-0', className)}>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="min-w-0 max-w-full">
          {eyebrow && <div className="mb-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-faint">{eyebrow}</div>}
          <h1
            className={cn('font-display font-normal tracking-[-0.02em] text-foreground [text-wrap:balance]', PAGE_HEADER_TITLE_SIZE[tier])}>
            {title}
          </h1>
        </div>
        {actions && <div className={cn('flex min-w-0 flex-wrap items-center gap-2.5', actionsClassName)}>{actions}</div>}
      </div>
      {description && <p className="mt-3 max-w-[60ch] text-base text-muted-foreground [text-wrap:pretty]">{description}</p>}
    </header>
  )
}
